import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import { ajustementsApi } from '@/api';
import AjustementFormModal from './AjustementFormModal';

const formatCoefficient = (ajustement) => {
  const sign = ajustement.sens === 'positif' ? '+' : '−';
  if (ajustement.type_ajustement === 'pourcentage') {
    return `${sign}${ajustement.valeur} %`;
  }
  const formatted = new Intl.NumberFormat('fr-FR').format(ajustement.valeur);
  return `${sign}${formatted} €`;
};

const NATURE_LABELS = {
  situation: 'Situation',
  etat: 'État',
  prestations: 'Prestations',
  superficie: 'Superficie',
  standing: 'Standing',
  occupation: 'Occupation',
  travaux: 'Travaux',
  environnement: 'Environnement',
  autre: 'Autre',
};

export default function AjustementTable({ ajustements, valorisationId, onRefresh }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const sorted = [...(ajustements || [])].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (ajustement) => {
    setEditing(ajustement);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    await ajustementsApi.delete(id);
    onRefresh();
  };

  const handleSave = async (data) => {
    if (editing) {
      await ajustementsApi.update(editing.id, data);
    } else {
      await ajustementsApi.create({ ...data, valorisation_id: valorisationId });
    }
    setModalOpen(false);
    setEditing(null);
    onRefresh();
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Ajustements</h3>
        <Button variant="secondary" size="sm" onClick={handleCreate}>
          <Plus className="w-4 h-4" />
          Ajouter
        </Button>
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-500 italic">Aucun ajustement.</p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Nature</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Coefficient</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Justification</th>
                <th className="px-4 py-2 text-right font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((aj) => (
                <tr key={aj.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-900">
                    {NATURE_LABELS[aj.nature] ?? aj.nature}
                  </td>
                  <td className="px-4 py-2 text-slate-900 font-medium">
                    {formatCoefficient(aj)}
                  </td>
                  <td className="px-4 py-2 text-slate-600 max-w-xs truncate">
                    {aj.justification || '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleEdit(aj)}
                        className="p-1.5 text-slate-500 hover:text-carex-600 hover:bg-slate-100 rounded transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(aj.id)}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <AjustementFormModal
          ajustement={editing}
          onSave={handleSave}
          onCancel={() => {
            setModalOpen(false);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}
